import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TSCard } from '@/components/shared/card/Card';
import { VerticalMenu } from '@/components/shared/menu/VerticalMenu';
import { FC } from 'react';
import { FiAlertTriangle, FiCalendar, FiClipboard, FiClock, FiHome, FiStar } from 'react-icons/fi';
import { GiCog } from 'react-icons/gi';
import { useNavigate, useParams } from 'react-router-dom';
import { NavPage } from '../../components/page/Breadcrumbs';
import { Tier, useGetSuperAdminOrganisationSettingsQuery } from '../../services/gql/graphql.generated';
import strings from './admin.translations';
import AdminGeneralSection from './general/AdminGeneralSection';
import { LeaveTypeSection } from './leaveType/AdminLeaveTypeSection';
import AdminLocationsSection from './locations/AdminLocationsSection';
import AdminSkillsSection from './skills/AdminSkillsSection';
import AdminTasksSection from './tasks/AdminTasksSection';

export const AdminPage: FC = () => {
  const [{ data }] = useGetSuperAdminOrganisationSettingsQuery();
  const { tabName } = useParams();
  const navigate = useNavigate();

  const breadcrumbs: NavPage[] = [
    {
      name: strings.page.admin,
      to: '/admin'
    }
  ];

  const isAdvancedLeaveEnabled = data?.superAdminOrganisationSettings?.leaveTypeTier === Tier.Advanced;

  const leaveTypesElement = isAdvancedLeaveEnabled ? (
    <LeaveTypeSection />
  ) : (
    <TSCard className="p-6 min-h-[240px] flex items-center justify-center">
      <div className="text-center flex flex-col items-center justify-center">
        <FiAlertTriangle className="h-16 w-16 text-amber-400" />
        <p className="mt-4 text-secondary-std">{strings.state.advancedLeaveDisabled}</p>
      </div>
    </TSCard>
  );

  const sideMenuOptions = [
    {
      icon: <GiCog className="h-5 w-5" />,
      label: strings.menu.general,
      anchorId: 'general',
      element: <AdminGeneralSection />
    },
    {
      icon: <FiHome className="h-5 w-5" />,
      label: strings.menu.locations,
      anchorId: 'locations',
      element: <AdminLocationsSection />
    },
    {
      icon: <FiCalendar className="h-5 w-5" />,
      label: strings.menu.leaveTypes,
      anchorId: 'leave-types',
      element: leaveTypesElement
    },
    {
      icon: <FiStar className="h-5 w-5" />,
      label: strings.menu.skills,
      anchorId: 'skills',
      element: <AdminSkillsSection />
    },
    {
      icon: <FiClipboard className="h-5 w-5" />,
      label: strings.menu.tasks,
      anchorId: 'tasks',
      element: <AdminTasksSection />
    }
  ];

  const allElements = (
    <>
      <AdminGeneralSection />
      <AdminLocationsSection />
      {leaveTypesElement}
      <AdminSkillsSection />
      <AdminTasksSection />
    </>
  );

  const selectedTab = tabName ?? sideMenuOptions[0].anchorId;
  const handleTabSelect = (anchorId: string) => {
    navigate(`/admin/${anchorId}`);
  };

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.page.title} icon={<FiClock />} breadcrumbs={breadcrumbs} />}
    >
      <PageSizer className="flex gap-5 flex-col lg:flex-row" mode={SizerMode.PAGE}>
        <div className="hidden lg:flex gap-5 w-full">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            isScroll={false}
            selectedTab={selectedTab}
          />
        </div>
        <div className="lg:hidden">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            allElements={allElements}
            isScroll={true}
            selectedTab={selectedTab}
          />
        </div>
      </PageSizer>
    </PageContainer>
  );
};
